import React from 'react';
import GridLayout from 'react-grid-layout';
import Graph from './Graph';
import '../assets/styles/DashboardTemplateView.css';
import { DASHBOARD_GRID_STATIC_PROPS, GRID_ROW_HEIGHT, getWatermarkSizeTier } from './dashboardGridConfig';

// "Copy to My Dashboard" is temporarily hidden. The copy logic
// (handleCopyToDashboard in Dashboard.js) is intact - flip this to re-enable.
const SHOW_COPY_TO_DASHBOARD = false;

/**
 * Calculates a static grid container height from a template's fixed
 * layout, same buffer logic as Dashboard.js's calculateDynamicHeight, but
 * derived once from the template's own layouts rather than tracked in state
 * (a template's layout never changes while it's being viewed).
 *
 * @param {Array} layouts - Template layout items
 * @returns {number} Height in pixels
 */
const calculateHeight = (layouts) => {
  if (!layouts || layouts.length === 0) return 600;
  let maxBottomY = 0;
  layouts.forEach((item) => {
    const bottomY = (item.y + item.h) * GRID_ROW_HEIGHT;
    if (bottomY > maxBottomY) maxBottomY = bottomY;
  });
  return Math.max(600, maxBottomY + 300);
};

/**
 * DashboardTemplateView - read-only preset dashboard view. Renders one of
 * the templates fetched from GET /getDashboardTemplates: a subtitle/badge/
 * copy-button header row, then the template's charts laid out exactly as
 * curated (desktop: non-interactive GridLayout; mobile: the same
 * single-column stack pattern the custom dashboard uses).
 */
const DashboardTemplateView = ({ template, isMobile, windowWidth, userChartIds, onCopyToDashboard, isCopying }) => {
  if (!template) {
    return null;
  }

  const { name, subtitle, charts, layouts } = template;
  const chartsToAdd = charts.filter((chart) => !userChartIds.has(chart.id));
  const allAlreadyAdded = charts.length > 0 && chartsToAdd.length === 0;
  const dynamicHeight = calculateHeight(layouts);

  return (
    <>
      {isMobile && <h1 className="ft-dashboard-mobile-title">{name}</h1>}

      <div className="dashboard-template-header">
        {subtitle && <p className="dashboard-template-subtitle">{subtitle}</p>}
        <div className="dashboard-template-actions">
          <span className="dashboard-template-readonly-badge">Read-only</span>
          {SHOW_COPY_TO_DASHBOARD && (
            <button
              className="ft-pill-btn dashboard-template-copy-button"
              onClick={() => onCopyToDashboard(template)}
              disabled={isCopying || allAlreadyAdded}
            >
              {allAlreadyAdded ? 'Already on My Dashboard' : isCopying ? 'Copying...' : 'Copy to My Dashboard'}
            </button>
          )}
        </div>
      </div>

      {isMobile ? (
        <div className="ft-dashboard-mobile-stack">
          {[...charts]
            .sort((a, b) => {
              const la = layouts.find((l) => l.i === a.id.toString());
              const lb = layouts.find((l) => l.i === b.id.toString());
              if (!la || !lb) return !la - !lb;
              return la.y - lb.y || la.x - lb.x;
            })
            .map((chart) => (
              <div key={chart.id.toString()} className="ft-mobile-chart-card">
                <Graph {...chart} showPlus={false} isEditMode={false} size="xs" />
              </div>
            ))}
        </div>
      ) : (
        <div className="dashboard-grid-container" style={{ minHeight: `${dynamicHeight}px` }}>
          <GridLayout
            {...DASHBOARD_GRID_STATIC_PROPS}
            className="dashboard-grid"
            layout={layouts}
            width={windowWidth}
            isDraggable={false}
            isResizable={false}
            style={{ height: `${dynamicHeight}px` }}
          >
            {charts.map((chart) => {
              const item = layouts.find((l) => l.i === chart.id.toString());
              const watermarkSize = item ? getWatermarkSizeTier(item.w, item.h, windowWidth) : undefined;

              return (
                <div key={chart.id.toString()} className="chart-wrapper">
                  <Graph {...chart} showPlus={false} isEditMode={false} size={watermarkSize} />
                </div>
              );
            })}
          </GridLayout>
        </div>
      )}
    </>
  );
};

export default DashboardTemplateView;
